import React, { useEffect, useState } from 'react'
import * as Sentry from "@sentry/react";

import { createClient, Session } from '@supabase/supabase-js'
import { Database } from '../../types/generated/public'
import { AppSupabaseClient } from '../../types/supabase'
import { AuthSessionEventDetail, expoSupportsAuth } from '../../lib/nativeBridge'
import {
  forwardTokenChangesToNative,
  installNativeSessionBackstops,
  listenForNativeSessionPushes,
  takeLegacyAuthTokenRaw,
} from '../../lib/nativeAuth'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || window.location.origin
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'test'

// Existing-install migration (see docs/plans/expo-owned-signin.md): must run
// BEFORE createClient below. Once the client exists with `persistSession:
// false` (set whenever expoSupportsAuth()), its own init path never touches
// localStorage, so a pre-Phase-2 webview-owned session has to be read raw
// here first or it's lost. `startupFlow.tsx`'s AwaitNativeSession state hands
// this to native if native comes back with no session of its own. The read
// consumes the stored entry — see takeLegacyAuthTokenRaw for why that matters
// once sign-out is in the picture.
export const legacySessionForNativeMigration: AuthSessionEventDetail | null =
  expoSupportsAuth() ? takeLegacyAuthTokenRaw(supabaseUrl) : null

// Native owns refresh; the webview must be structurally incapable of
// refreshing (autoRefreshToken: false) or persisting its own copy
// (persistSession: false) — two clients independently refreshing the same
// refresh token would each invalidate the other's. Only applies once native
// actually advertises support for the handoff (expoSupportsAuth(), not the
// broader isExpoApp()) — gating on isExpoApp() would break every
// already-installed native build the moment this ships, before any native
// rebuild lands, by disabling persistence for a session native doesn't yet
// know how to hand back.
const supabaseAuthOptions = expoSupportsAuth()
  ? { auth: { autoRefreshToken: false, persistSession: false, detectSessionInUrl: false } }
  : undefined

export const supabase: AppSupabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, supabaseAuthOptions)

interface SupabaseContext {
  online: boolean
  supabase: AppSupabaseClient,
  session: Session | null
}

const supabaseContext = React.createContext<SupabaseContext>({
  online: false,
  supabase,
  session: null
})

// Export for testing/storybook
export const SupabaseContext = supabaseContext

export function SupabaseProvider({children}: React.PropsWithChildren) {

  return <SupabaseContextProvider client={supabase}>
      {children}
    </SupabaseContextProvider>
}

function SupabaseContextProvider({ children, client }: { children: React.ReactNode, client: AppSupabaseClient }) {
  const [session, setSession] = useState<Session | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [online, setOnline] = useState(true)

  useEffect(() => {
    // Must be registered before anything can call requestAuthSession() (the
    // AwaitNativeSession startup state, mounted only once this provider's
    // child tree renders) — see listenForNativeSessionPushes's doc for why
    // a single, app-lifetime listener (rather than a one-shot one per
    // request) is required: it also carries unsolicited pushes (refresh,
    // post-migration adoption), not just handshake replies. No-ops (never
    // fires) when !expoSupportsAuth(), since native never dispatches
    // auth-session in that case.
    const unsubscribeNativeSessionPushes = listenForNativeSessionPushes(client)

    // The pull half of the token-freshness design (Phase 3): re-ask native for
    // its session when the app returns to the foreground, or when anything
    // comes back 401. Installs nothing at all when !expoSupportsAuth().
    const uninstallNativeSessionBackstops = installNativeSessionBackstops()

    // We ought to get an onAuthStateChange when the session is loaded/refreshed
    // If we don't something went wrong
    const timeout = setTimeout(() => {
      setInitialized(true)
    }, 5000)

    const {data: {subscription}} = client.auth.onAuthStateChange((event, session) => {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Auth state changed',
        level: 'info',
        data: {
          event,
          // Identifiers only — the email and phone that used to be here are
          // HIPAA identifiers, and the user id is enough to correlate with.
          user: session?.user ? {
            id: session.user.id,
            aud: session.user.aud,
          } : undefined,
        }
      })
      
      // Allow access to the `/admin` path if the user is logged in
      if (session) {
        document.cookie = `auth_token=${session.access_token}; max-age=${session.expires_in}; path=/admin; Secure; SameSite=Strict`
      } else {
        document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/admin;'
      }
      
      // The scope user rides along on every event *and* every performance
      // transaction, so an email here would identify the member behind all of
      // it — which is what makes the opaque UUIDs in our routes worth having.
      Sentry.setUser(session?.user ? { id: session.user.id } : null)

      // Forward any token the webview itself minted (e.g. MFA step-up) back to
      // native — see forwardTokenChangesToNative's echo-guard doc for why this
      // doesn't loop on a native-pushed session. No-ops via postToNative's own
      // expoSupportsAuth() gate when native doesn't support the handoff.
      forwardTokenChangesToNative(session)

      setSession(session)
      setInitialized(true)
      clearTimeout(timeout)
    })

    return () => {
      subscription.unsubscribe()
      unsubscribeNativeSessionPushes()
      uninstallNativeSessionBackstops()
      clearTimeout(timeout)
    }
  }, [client])


  if (!initialized) {
    return null;
  }

  return <supabaseContext.Provider value={{
        online,
        supabase: client,
        session
      }}>
      {children}
    </supabaseContext.Provider>
}

/**
 * Returns true if the user is connected to the Supabase server
 */
export function useIsOnline() {
  //return React.useContext(supabaseContext).online

  // Disable this for now
  return true
}

export const useClient = () => React.useContext(supabaseContext).supabase

export const useSession = () => React.useContext(supabaseContext).session
